import {
  Button,
  Dialog,
  Field,
  Input,
  NativeSelect,
  Portal,
  Stack,
  Text,
} from '@chakra-ui/react';
import { toaster } from '@mono/chakra';
import { isHttpError } from '@mono/http-hook';
import { FC, useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import type { OrganizationRoleValue } from '../session/useSession';
import { ORGANIZATION_ROLE_OPTIONS } from '../organizations/roles';

export type ManageUserFormValues = {
  email: string;
  role: OrganizationRoleValue;
};

type ManageUserModalProps = {
  isOpen: boolean;
  mode: 'create' | 'edit';
  defaultValues?: ManageUserFormValues;
  onClose: () => void;
  onSubmit: (values: ManageUserFormValues) => Promise<void>;
};

const getErrorMessage = (error: unknown, fallback: string) => {
  if (isHttpError(error)) {
    const responseData = error.response?.data;
    if (responseData && typeof responseData === 'object') {
      const { error: errorMessage, message: responseMessage } =
        responseData as {
          error?: string;
          message?: string;
        };
      return errorMessage ?? responseMessage ?? error.message ?? fallback;
    }
    return error.message ?? fallback;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return fallback;
};

const ManageUserModal: FC<ManageUserModalProps> = ({
  isOpen,
  mode,
  defaultValues,
  onClose,
  onSubmit,
}) => {
  const {
    handleSubmit,
    register,
    reset,
    formState,
  } = useForm<ManageUserFormValues>({
    defaultValues: {
      email: defaultValues?.email ?? '',
      role: defaultValues?.role ?? 'WRITE',
    },
  });
  const [serverError, setServerError] = useState<string | null>(null);

  useEffect(() => {
    reset({
      email: defaultValues?.email ?? '',
      role: defaultValues?.role ?? 'WRITE',
    });
    setServerError(null);
  }, [defaultValues, reset, isOpen]);

  const handleFormSubmit: SubmitHandler<ManageUserFormValues> = async (values) => {
    setServerError(null);

    try {
      await onSubmit(values);
      toaster.create({
        type: 'success',
        title: mode === 'create' ? 'User added' : 'User updated',
        description:
          mode === 'create'
            ? 'User has been added to the organization.'
            : 'User details were updated successfully.',
      });
      onClose();
    } catch (error) {
      const message = getErrorMessage(
        error,
        mode === 'create' ? 'Unable to add user.' : 'Unable to update user.'
      );
      setServerError(message);
      toaster.create({
        type: 'error',
        title: mode === 'create' ? 'Failed to add user' : 'Failed to update user',
        description: message,
      });
    }
  };

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={({ open }) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.CloseTrigger />
            <Dialog.Header flexDirection={'column'}>
              <Dialog.Title>
                {mode === 'create' ? 'Add user' : 'Edit user'}
              </Dialog.Title>
              <Dialog.Description>
                {mode === 'create'
                  ? 'Add someone who already has an account.'
                  : 'Update the role for this member.'}
              </Dialog.Description>
            </Dialog.Header>
            <Dialog.Body>
              <form id="manage-user-form" onSubmit={handleSubmit(handleFormSubmit)}>
                <Stack gap="4">
                  <Field.Root invalid={Boolean(formState.errors.email)}>
                    <Field.Label>Email</Field.Label>
                    <Input
                      type="email"
                      placeholder="leslie@pawnee.gov"
                      disabled={mode === 'edit'}
                      {...register('email', {
                        required: 'Email is required.',
                        pattern: {
                          value: /[^\s@]+@[^\s@]+\.[^\s@]+/,
                          message: 'Enter a valid email address.',
                        },
                      })}
                    />
                    <Field.HelperText>
                      {mode === 'create'
                        ? 'User must already exist in system. Ask them to sign up first.'
                        : 'Email cannot be changed for existing users.'}
                    </Field.HelperText>
                    <Field.ErrorText>
                      {formState.errors.email?.message}
                    </Field.ErrorText>
                  </Field.Root>
                  <Field.Root invalid={Boolean(formState.errors.role)}>
                    <Field.Label>Role</Field.Label>
                    <NativeSelect.Root>
                      <NativeSelect.Field
                        {...register('role', {
                          required: 'Role is required.',
                        })}
                      >
                        {ORGANIZATION_ROLE_OPTIONS.map((option) => (
                          <option value={option.value} key={option.value}>
                            {option.label} - {option.description}
                          </option>
                        ))}
                      </NativeSelect.Field>
                      <NativeSelect.Indicator />
                    </NativeSelect.Root>
                    <Field.HelperText>
                      Maintainers and Admins can manage users and settings.
                    </Field.HelperText>
                    <Field.ErrorText>
                      {formState.errors.role?.message}
                    </Field.ErrorText>
                  </Field.Root>
                  {serverError ? (
                    <Text color="fg.error" fontSize="sm">
                      {serverError}
                    </Text>
                  ) : null}
                </Stack>
              </form>
            </Dialog.Body>
            <Dialog.Footer gap="3" justifyContent="flex-end">
              <Button
                variant="ghost"
                onClick={onClose}
                disabled={formState.isSubmitting}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                form="manage-user-form"
                loading={formState.isSubmitting}
                colorPalette="primary"
              >
                {mode === 'create' ? 'Add user' : 'Save changes'}
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default ManageUserModal;
